import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Button, Box, TextField, Select, MenuItem, Modal, CircularProgress, Autocomplete } from '@mui/material';
import Logo from "../../images/logo/Logo.svg";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { API_CONFIG } from "../../api/apiUrl"
import { motion } from 'framer-motion';
import { trackRegistration } from "../../utils/metaPixel";
const contentVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 },
};

const contentTransition = {
  type: 'spring',
  stiffness: 120,
  damping: 20,
  duration: 0.3,
};



interface State {
  id: number;
  name: string;
  isActive: boolean;
  isDeleted: boolean;
}

interface City {
  id: number;
  name: string;
  isActive: boolean;
  isDeleted: boolean;
  states?: {
    id: number;
    name: string | null;
    isActive: boolean | null;
    isDeleted: boolean | null;
  };
}

interface FormData {
  name: string;
  mobile: string;
  email: string;
  stateId: string;
  cityId: string;
  authorities: string[];
  password?: string;
}

const Signup = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const role = state?.role || 'ROLE_USER';
  const [loading, setLoading] = useState<boolean>(false);
  const [verifyLoading, setVerifyLoading] = useState<boolean>(false);
  const [resendOtpLoading, setResendOtpLoading] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    mobile: '+91-',
    email: '',
    cityId: '',
    stateId: '',
    authorities: [role],
    password: 'HARDCODED',
  });

  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [showModal, setShowModal] = useState<boolean>(false);

  const [nameError, setNameError] = useState<string>('');
  const [mobileError, setMobileError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [locationError, setLocationError] = useState<string>('');
  const [stateError, setStateError] = useState<string>('');
  const [otpError, setOtpError] = useState<string>('');

  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);

  const fetchStates = async () => {
    try {
      const response = await axios.get<State[]>(API_CONFIG.STATES);
      setStates(response.data);
    } catch (err) {
      // console.log('Error:', err);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get<City[]>(API_CONFIG.CITIES);
      setCities(response.data);
    } catch (err) {
      // console.log('Error:', err);
    }
  };

  useEffect(() => {
    fetchStates();
    fetchCities();
  }, []);

  useEffect(() => {
    if (formData.stateId) {
      const stateIdNum = parseInt(formData.stateId);
      const filtered = cities.filter(city =>
        city.states && city.states.id === stateIdNum
      );
      setFilteredCities(filtered);
    } else {
      setFilteredCities([]);
    }
  }, [formData.stateId, cities]);

  const startResendCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      let mobileValue = value;
      if (!mobileValue.startsWith('+91-')) mobileValue = '+91-';
      const number = mobileValue.substring(4).replace(/\D/g, '').slice(0, 10);
      setFormData({ ...formData, mobile: '+91-' + number });
      setMobileError(number.length === 10 ? '' : 'Mobile number must be 10 digits');
    } else {
      setFormData({ ...formData, [name]: value });
      if (name === 'stateId') setFormData(prev => ({ ...prev, cityId: '' }));
      validateField(name, value);
    }
  };

  const handleOtpChange = (index: number) => (e: any) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError('');
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: any) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const validateField = (name: string, value: string) => {
    if (name === 'name') setNameError(value ? '' : 'Name is required');
    if (name === 'email') setEmailError(value ? (value.includes('@') ? '' : 'Invalid email') : 'Email is required');
    if (name === 'stateId') setStateError(value ? '' : 'state is required');
    if (name === 'cityId') setLocationError(value ? '' : 'location is required');
  };

  const isFormValid = () => {
    return (
      formData.name &&
      formData.mobile.length === 14 &&
      formData.email &&
      formData.email.includes('@') &&
      formData.cityId &&
      formData.stateId &&
      !nameError &&
      !mobileError &&
      !emailError &&
      !locationError &&
      !stateError
    );
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    validateField('name', formData.name);
    validateField('email', formData.email);
    validateField('stateId', formData.stateId);
    validateField('cityId', formData.cityId);
    const number = formData.mobile.substring(4);
    if (number.length !== 10) {
      setMobileError('Mobile number must be 10 digits');
      return;
    }
    if (!isFormValid()) return;
    setLoading(true);
    try {
      const mobileNum = parseInt(number);
      const nameParts = formData.name.split(' ');
      const selectedState = states.find(s => s.id === parseInt(formData.stateId));
      const selectedCity = filteredCities.find(c => c.id === parseInt(formData.cityId));
      await axios.post(API_CONFIG.REGISTER, {
        mobile: mobileNum,
        cities: {
          id: selectedCity!.id,
          name: selectedCity!.name,
          isActive: selectedCity!.isActive,
          isDeleted: selectedCity!.isDeleted,
          states: {
            id: selectedState!.id,
            name: selectedState!.name,
            isActive: selectedState!.isActive,
            isDeleted: selectedState!.isDeleted,
          },
        },
        login: mobileNum.toString(),
        firstName: nameParts[0],
        lastName: nameParts.slice(1).join(' ') || nameParts[0],
        email: formData.email,
        authorities: formData.authorities,
        password: formData.password,
      });
      toast.success('Registration successful! OTP sent to your email.');
      setShowModal(true);
      startResendCooldown();
    } catch (err: any) {
      toast.error('Mobile Number already used!');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setResendOtpLoading(true);
    const number = formData.mobile.substring(4);
    try {
      await axios.post(API_CONFIG.GET_OTP, { mobile: parseInt(number) });
      startResendCooldown();
      toast.success('OTP resent!');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setResendOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.every(digit => digit)) {
        toast.error('Please enter all 6 digits');
        return;
    }
    setVerifyLoading(true);
    const number = formData.mobile.substring(4);
    try {
        const response = await axios.post(API_CONFIG.VERIFY_OTP, {
            mobile: parseInt(number),
            otp: parseInt(otp.join('')),
            message: "string"
        });
        if (response.status === 200 || response.status === 201) {
            const userId = response.data?.id;
            setShowModal(false);
            navigate('/signin', {
                state: {
                    userId,
                    mobile: formData.mobile,
                    authorities: formData.authorities
                }
            });
            toast.success('OTP verified!');
            trackRegistration();
            setOtpError('');
        }
    } catch (err: any) {
        toast.error( 'Invalid OTP');
    } finally {
        setVerifyLoading(false);
    }
  };

  return (

    <motion.div
      variants={contentVariants}
      initial="initial"
      animate="in"
      exit="out"
      transition={contentTransition}
    >
      <Box sx={styles.mainContainer}>
        <Toaster position="top-right" reverseOrder={false} />
        <Card sx={styles.card}>
          <CardContent>
            <Box sx={styles.logoContainer}>
              <img src={Logo} alt="Logo" style={styles.logo} />
            </Box>
            <Box sx={styles.buttonContainer}>
              <Link to="/signin">
                <Button sx={styles.SignInButton}>Sign In</Button>
              </Link>
              <Button sx={styles.SignUpButton}>Sign Up</Button>
            </Box>
            <form onSubmit={handleSubmit}>
              <Box sx={{ mb: 2 }}>
                <Typography sx={styles.inputLabel}>Name</Typography>
                <TextField name="name" onChange={handleChange} value={formData.name} fullWidth placeholder="Alexa Fariz" variant="outlined" size="small" sx={styles.textField} error={!!nameError} helperText={nameError} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography sx={styles.inputLabel}>Mobile Number</Typography>
                <TextField name="mobile" onChange={handleChange} value={formData.mobile} fullWidth placeholder="+91-9999999999" variant="outlined" size="small" sx={styles.textField} error={!!mobileError} helperText={mobileError} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography sx={styles.inputLabel}>Email</Typography>
                <TextField name="email" onChange={handleChange} value={formData.email} fullWidth placeholder="example@gmail.com" variant="outlined" size="small" sx={styles.textField} error={!!emailError} helperText={emailError} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography sx={styles.inputLabel}>State</Typography>
                <Autocomplete
                  options={states}
                  getOptionLabel={(option) => option.name}
                  value={states.find(state => state.id === parseInt(formData.stateId)) || null}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, stateId: newValue ? String(newValue.id) : '', cityId: '' });
                    validateField('stateId', newValue ? String(newValue.id) : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select State"
                      variant="outlined"
                      size="small"
                      sx={styles.textField}
                      error={!!stateError}
                      helperText={stateError}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography sx={styles.inputLabel}>Location</Typography>
                <Autocomplete
                  options={filteredCities}
                  getOptionLabel={(option) => option.name}
                  value={filteredCities.find(city => city.id === parseInt(formData.cityId)) || null}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, cityId: newValue ? String(newValue.id) : '' });
                    validateField('cityId', newValue ? String(newValue.id) : '');
                  }}
                  disabled={!formData.stateId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Location"
                      variant="outlined"
                      size="small"
                      sx={styles.textField}
                      error={!!locationError}
                      helperText={locationError}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Box>
              <Box sx={styles.submitContainer}>
                <Button
                  type="submit"
                  sx={styles.submitButton}
                  disabled={!isFormValid() || loading}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Continue'}
                  
                </Button>
              </Box>
              <Box sx={styles.signInLinkContainer}>
                <Typography sx={styles.alreadyHaveAccountText}>
                  Already have an account?{' '}
                  <Link to="/signin" style={styles.signInLink}>
                    Sign In
                  </Link>
                </Typography>
              </Box>
            </form>
          </CardContent>
        </Card>
        <Modal open={showModal} onClose={() => setShowModal(false)}>
          <Box sx={modalStyles}>
            <Typography variant="h5" sx={{ mb: 1, textAlign: 'center', fontWeight: 600, color: '#333' }}>
              Verify Your OTP
            </Typography>
            <Typography sx={{ mb: 2, textAlign: 'center', color: '#757575', fontSize: { xs: '12px', md: '14px' } }}>
              Enter the 6-digit code sent to your mobile number or email
            </Typography>
            <Box sx={{ display: 'flex', gap: { xs: 1, md: 1.5 }, justifyContent: 'center', mb: 3 }}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <TextField
                  key={i}
                  id={`otp-input-${i}`}
                  value={otp[i]}
                  onChange={handleOtpChange(i)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  inputProps={{
                    maxLength: 1,
                    style: { textAlign: 'center', fontWeight: 500 },
                    inputMode: "numeric"
                  }}
                  variant="outlined"
                  sx={{
                    width: { xs: '36px', md: '48px' },
                    height: { xs: '36px', md: '48px' },
                    fontSize: { xs: '18px', md: '24px' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      height: '100%',
                      bgcolor: '#f5f5f5',
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A', borderWidth: '2px' }
                    }
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, md: 2 } }}>
              <Button
                onClick={handleResendOtp}
                sx={styles.resendButton}
                disabled={resendCooldown > 0 || resendOtpLoading}
              >
                {resendOtpLoading ? <CircularProgress size={20} color="inherit" /> : (resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP')}
              </Button>
              <Button
                onClick={handleVerifyOtp}
                disabled={!otp.every((digit) => digit) || verifyLoading}
                sx={styles.submitButton}
              >
                {verifyLoading ?  <CircularProgress size={24} sx={{ color: 'white' }} />: 'Verify OTP'}
              </Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    </motion.div>
  );
};

const styles: any = {
  mainContainer: { my: { xs: 2, md: 5 }, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 1, md: 2 } },
  card: { boxShadow: "0px 0px 4px 0px #00000033", width: { xs: '100%', md: '450px' }, maxWidth: 450, p: { xs: 1.5, md: 2.5 }, borderRadius: '20px' },
  logoContainer: { display: 'flex', justifyContent: 'center', mb: { xs: 2, md: 2.5 } },
  logo: { width: { xs: '90px', md: '120px' } },
  buttonContainer: { display: 'flex', justifyContent: 'center', mb: { xs: 2, md: 2.5 }, gap: { xs: 2, md: 4 }, flexWrap: 'wrap' },
  SignInButton: { textTransform: 'capitalize', fontSize: { xs: '14px', md: '15px' }, fontWeight: 500, borderRadius: '24px', border: '1px solid black', color: '#000', py: 0.5, px: { xs: 3, md: 4 } },
  SignUpButton: { textTransform: 'capitalize', fontSize: { xs: '14px', md: '15px' }, fontWeight: 500, borderRadius: '24px', color: 'white', bgcolor: '#B69D74', '&:hover': { bgcolor: '#B69D74' }, px: { xs: 3, md: 4 }, py: 0.5 },
  textField: { '& .MuiOutlinedInput-root': { borderRadius: '50px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' } }, fontSize: { xs: '13px', md: '14px' } },
  inputLabel: { fontSize: { xs: '13px', md: '14px' }, fontWeight: 400, mb: { xs: 0.75, md: 1 }, color: '#212121' },
  select: { borderRadius: '50px', '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #B69D74' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B69D74' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#B69D74' }, '& .MuiSelect-select': { py: '8px' } },
  submitContainer: { display: 'flex', justifyContent: 'center', mt: { xs: 2, md: 2.5 } },
  submitButton: {
    textTransform: 'capitalize',
    fontSize: { xs: '16px', md: '16px' },
    fontWeight: 600,
    borderRadius: '28px',
    color: 'white',
    bgcolor: '#89652A',
    py: 0.7,
    px: { xs: 4, md: 6 },
    '&:hover': { bgcolor: '#6b4e20' },
    '&:disabled': { bgcolor: '#a88b5e' },
  },
  resendButton: { textTransform: 'capitalize', fontSize: { xs: '13px', md: '14px' }, fontWeight: 400, color: '#212121', py: 0.5, px: { xs: 2, md: 3 } },
  signInLinkContainer: { textAlign: 'center', mt: 2 },
  alreadyHaveAccountText: { fontSize: '14px', color: '#666' },
  signInLink: { color: '#89652A', textDecoration: 'none', fontWeight: 600 }
};

const modalStyles = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '85%', sm: '85%', md: '85%' },
  maxWidth: '350px',
  bgcolor: 'white',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  p: { xs: 2.5, md: 3 },
  border: '1px solid #e0e0e0'
};

export default Signup;